import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Alert,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import SmsIcon from '@mui/icons-material/Sms';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import { useSignupWizard } from '../../../contexts/SignupWizardContext';
import {
  startTotpMfaSetup,
  completeTotpMfaSetup,
  startSmsMfaSetup,
  verifySmsMfaSetup,
  completeSignup,
} from '../../../utils/signupApi';
import { featureFlags } from '../../../utils/featureFlags';
import { COUNTRY_CODES } from '../../../constants/phoneCountryCodes';
import SMSConsentDisclosure from '../../../components/SMSConsentDisclosure';

type MfaMethod = 'totp' | 'sms' | null;

interface AuthTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

interface SignupStep6MFAProps {
  onComplete: (authTokens?: AuthTokens) => void;
  onBack: () => void;
}

const isSessionExpiredError = (error: Error): boolean => {
  const message = error.message.toLowerCase();
  return message.includes('expired') || message.includes('mfa_auth_required');
};

export const SignupStep6MFA: React.FC<SignupStep6MFAProps> = ({ onComplete, onBack }) => {
  const { state, setMfaConfigured, setMfaMethod } = useSignupWizard();
  const smsEnabled = featureFlags.isEnabled('enableSmsMfa');

  const [selectedMethod, setSelectedMethod] = useState<MfaMethod>(smsEnabled ? null : 'totp');
  const [setupStep, setSetupStep] = useState<'select' | 'setup' | 'verify'>(smsEnabled ? 'select' : 'setup');
  const [localError, setLocalError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSessionExpired, setIsSessionExpired] = useState(false);

  // TOTP setup state
  const [totpSecret, setTotpSecret] = useState<string | null>(null);
  const [totpQrCode, setTotpQrCode] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [qrCodeFailed, setQrCodeFailed] = useState(false);
  const [showManualCode, setShowManualCode] = useState(false);

  // SMS setup state
  const [countryCode, setCountryCode] = useState('+1');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [smsSent, setSmsSent] = useState(false);

  // Auto-trigger TOTP setup when SMS is disabled (method selection is skipped)
  useEffect(() => {
    if (!smsEnabled && selectedMethod === 'totp' && !totpSecret && !isProcessing) {
      handleTotpSetup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectMethod = (method: MfaMethod) => {
    setSelectedMethod(method);
    setSetupStep('setup');
    setLocalError(null);

    if (method === 'totp') {
      handleTotpSetup();
    }
    // SMS requires phone number input first
  };

  const handleTotpSetup = async () => {
    if (!state.signupToken) {
      setLocalError('Session expired. Please start over.');
      return;
    }

    setIsProcessing(true);
    setLocalError(null);

    try {
      const response = await startTotpMfaSetup(state.signupToken);

      if (response.success && response.secret) {
        setTotpSecret(response.secret);
        setTotpQrCode(response.qrCode || null);
        setSetupStep('verify');
      } else {
        throw new Error(response.error || 'Failed to start TOTP setup');
      }
    } catch (error) {
      const err = error as Error;
      if (isSessionExpiredError(err)) {
        setIsSessionExpired(true);
        setLocalError('Your session has expired. Please go back and re-enter your password.');
      } else {
        setLocalError(err.message || 'TOTP setup failed');
      }
      setSetupStep('select');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleTotpVerify = async () => {
    if (!state.signupToken || !totpCode || totpCode.length !== 6) {
      setLocalError('Please enter a valid 6-digit code');
      return;
    }

    setIsProcessing(true);
    setLocalError(null);

    try {
      const response = await completeTotpMfaSetup(state.signupToken, totpCode);

      if (response.success && response.mfaEnabled) {
        await finalizeMfaSetup('totp', response.authTokens);
      } else {
        throw new Error(response.error || 'Invalid verification code');
      }
    } catch (error) {
      setLocalError((error as Error).message || 'TOTP verification failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSmsSendCode = async () => {
    if (!state.signupToken) {
      setLocalError('Session expired. Please start over.');
      return;
    }

    // Basic phone number validation
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    if (cleanPhone.length < 10) {
      setLocalError('Please enter a valid phone number');
      return;
    }

    // Format as E.164 using selected country code
    const formattedPhone = `${countryCode}${cleanPhone}`;

    setIsProcessing(true);
    setLocalError(null);

    try {
      const response = await startSmsMfaSetup(state.signupToken, formattedPhone);

      if (response.success) {
        setSmsSent(true);
        setSetupStep('verify');
      } else {
        throw new Error(response.error || 'Failed to send verification code');
      }
    } catch (error) {
      const err = error as Error;
      if (isSessionExpiredError(err)) {
        setIsSessionExpired(true);
        setLocalError('Your session has expired. Please go back and re-enter your password.');
        setSetupStep('select');
      } else {
        setLocalError(err.message || 'Failed to send SMS');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSmsVerify = async () => {
    if (!state.signupToken || !smsCode || smsCode.length !== 6) {
      setLocalError('Please enter a valid 6-digit code');
      return;
    }

    setIsProcessing(true);
    setLocalError(null);

    try {
      const response = await verifySmsMfaSetup(state.signupToken, smsCode);

      if (response.success && response.mfaEnabled) {
        await finalizeMfaSetup('sms', response.authTokens);
      } else {
        throw new Error(response.error || 'Invalid verification code');
      }
    } catch (error) {
      setLocalError((error as Error).message || 'SMS verification failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSkipMfa = async () => {
    if (!state.signupToken) {
      setLocalError('Session expired. Please start over.');
      return;
    }
    setIsProcessing(true);
    setLocalError(null);
    try {
      const response = await completeSignup(state.signupToken);
      if (response.success) {
        onComplete();
      } else {
        throw new Error(response.error || 'Failed to complete signup');
      }
    } catch (error) {
      setLocalError((error as Error).message || 'Failed to skip MFA setup');
    } finally {
      setIsProcessing(false);
    }
  };

  const finalizeMfaSetup = async (method: 'totp' | 'sms', authTokens?: AuthTokens) => {
    if (!state.signupToken) {
      setLocalError('Session expired. Please start over.');
      return;
    }

    try {
      const response = await completeSignup(state.signupToken);

      if (response.success) {
        setMfaConfigured(true);
        setMfaMethod(method);  // Track which MFA method was used for success page messaging
        onComplete(authTokens);
      } else {
        throw new Error(response.error || 'Failed to complete signup');
      }
    } catch (error) {
      setLocalError((error as Error).message || 'Failed to complete signup');
    }
  };

  const renderMethodSelection = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Set Up Two-Factor Authentication
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Secure your account with two-factor authentication. Choose your preferred method,
        or skip and set it up later from Settings.
      </Typography>

      {localError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {localError}
        </Alert>
      )}

      {isSessionExpired && (
        <Button
          variant="contained"
          onClick={onBack}
          sx={{ mb: 2 }}
        >
          Go Back to Password
        </Button>
      )}

      <Grid container spacing={2} justifyContent="center">
        <Grid item xs={12} md={6}>
          <Card
            variant="outlined"
            sx={{
              height: '100%',
              border: selectedMethod === 'totp' ? '2px solid' : '1px solid',
              borderColor: selectedMethod === 'totp' ? 'primary.main' : 'divider',
            }}
          >
            <CardActionArea onClick={() => handleSelectMethod('totp')} sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <PhoneAndroidIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Authenticator App
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use Google Authenticator, Authy, etc.
                </Typography>
                <Alert severity="info" sx={{ mt: 2 }}>
                  Recommended
                </Alert>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        {smsEnabled && (
          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                border: selectedMethod === 'sms' ? '2px solid' : '1px solid',
                borderColor: selectedMethod === 'sms' ? 'primary.main' : 'divider',
              }}
            >
              <CardActionArea onClick={() => handleSelectMethod('sms')} sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <SmsIcon sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    SMS
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Receive codes via text message
                  </Typography>
                  <Alert severity="warning" sx={{ mt: 2 }} icon={<WarningIcon />}>
                    Less Secure
                  </Alert>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        )}
      </Grid>

      <Alert severity="info" sx={{ mt: 3 }} icon={<InfoIcon />}>
        <Typography variant="body2">
          You can manage your MFA settings after completing signup.
        </Typography>
      </Alert>

      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Button
          variant="text"
          onClick={handleSkipMfa}
          disabled={isProcessing}
          sx={{ textTransform: 'none', color: 'text.secondary' }}
        >
          Skip for now →
        </Button>
      </Box>
    </Box>
  );

  const renderTotpSetup = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Set Up Authenticator App
      </Typography>

      {localError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {localError}
        </Alert>
      )}

      {isProcessing ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Setting up authenticator...</Typography>
        </Box>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            1. Open your authenticator app (Google Authenticator, Authy, etc.)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            2. Scan this QR code:
          </Typography>

          {totpQrCode && !qrCodeFailed && (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <img
                src={totpQrCode}
                alt="QR Code"
                style={{ maxWidth: 200 }}
                onError={() => setQrCodeFailed(true)}
              />
            </Box>
          )}

          {(qrCodeFailed || !totpQrCode) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              QR code failed to load. Please enter the code below manually in your authenticator app.
            </Alert>
          )}

          {totpQrCode && !qrCodeFailed && !showManualCode && (
            <Button
              variant="text"
              size="small"
              onClick={() => setShowManualCode(true)}
              sx={{ mb: 2, textTransform: 'none' }}
            >
              Can&apos;t scan? Show manual code
            </Button>
          )}

          {totpSecret && (showManualCode || qrCodeFailed || !totpQrCode) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Manual setup code: <code>{totpSecret}</code>
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            3. Enter the 6-digit code from your app:
          </Typography>

          <TextField
            fullWidth
            label="Verification Code"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            margin="normal"
            inputMode="numeric"
            placeholder="000000"
            inputProps={{ maxLength: 6 }}
          />

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button variant="outlined" onClick={() => setSetupStep('select')}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleTotpVerify}
              disabled={totpCode.length !== 6 || isProcessing}
            >
              {isProcessing ? 'Verifying...' : 'Verify & Complete'}
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button
              variant="text"
              onClick={handleSkipMfa}
              disabled={isProcessing}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              Skip for now →
            </Button>
          </Box>
        </>
      )}
    </Box>
  );

  const renderSmsSetup = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Set Up SMS Authentication
      </Typography>

      <Alert severity="warning" sx={{ mb: 3 }} icon={<WarningIcon />}>
        SMS authentication is vulnerable to SIM swapping attacks. We recommend using an
        authenticator app for better security.
      </Alert>

      {localError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {localError}
        </Alert>
      )}

      {!smsSent ? (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter your phone number to receive verification codes via SMS.
          </Typography>

          <SMSConsentDisclosure />

          <FormControl fullWidth margin="normal">
            <InputLabel id="signup-country-code-label">Country Code</InputLabel>
            <Select
              labelId="signup-country-code-label"
              id="signup-country-code-select"
              value={countryCode}
              label="Country Code"
              onChange={(e) => setCountryCode(e.target.value)}
              disabled={isProcessing}
            >
              {COUNTRY_CODES.map((country) => (
                <MenuItem key={country.code} value={country.code}>
                  {country.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Phone Number"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
            margin="normal"
            placeholder="5551234567"
            helperText={
              phoneNumber.replace(/\D/g, '').length >= 10
                ? `Will send to: ${countryCode} (${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`
                : 'Enter 10-digit phone number (digits only)'
            }
          />

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button variant="outlined" onClick={() => setSetupStep('select')}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleSmsSendCode}
              disabled={phoneNumber.length < 10 || isProcessing}
            >
              {isProcessing ? 'Sending...' : 'Send Code'}
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button
              variant="text"
              onClick={handleSkipMfa}
              disabled={isProcessing}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              Skip for now →
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the 6-digit code sent to your phone.
          </Typography>

          <TextField
            fullWidth
            label="Verification Code"
            value={smsCode}
            onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            margin="normal"
            inputMode="numeric"
            placeholder="000000"
            inputProps={{ maxLength: 6 }}
          />

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setSmsSent(false);
                setSmsCode('');
              }}
            >
              Change Number
            </Button>
            <Button
              variant="contained"
              onClick={handleSmsVerify}
              disabled={smsCode.length !== 6 || isProcessing}
            >
              {isProcessing ? 'Verifying...' : 'Verify & Complete'}
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button
              variant="text"
              onClick={handleSkipMfa}
              disabled={isProcessing}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              Skip for now →
            </Button>
          </Box>
        </>
      )}
    </Box>
  );

  if (setupStep === 'select') {
    return renderMethodSelection();
  }

  if (selectedMethod === 'totp') {
    return renderTotpSetup();
  }

  if (selectedMethod === 'sms') {
    return renderSmsSetup();
  }

  return renderMethodSelection();
};

export default SignupStep6MFA;
